import { NavArrowDown, Trash } from 'iconoir-react'
import { ReactElement, ReactNode, useMemo } from 'react'
import { Button } from 'app/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import type { StageType } from 'app/types/entities'

// The dynamic `border-pipelines-*` classes are generated centrally via the
// `@source inline(...)` directive in src/styles/tailwind.css — no per-file safelist needed.

interface PipelineStageDropdownProps {
  stages: StageType[]
  currentStage: string
  onChange: (stageName: string) => void
  /** When set, appends a destructive "Remove from pipeline" item below the stages. */
  onRemove?: () => void
  triggerClassName?: string
  /** Custom trigger content (e.g. a whole table chip), styled by `triggerClassName`. Defaults to a field button. */
  children?: ReactNode
}

/**
 * Inline stage picker shared by the inbox contact card, the contact drawer, and
 * the contacts-table pipelines cell. Renders the current stage (or custom
 * children) as a trigger; the menu portals to `body` (Radix) so it escapes any
 * clipping/overflow container it's rendered inside.
 */
export function PipelineStageDropdown({
  stages,
  currentStage,
  onChange,
  onRemove,
  triggerClassName,
  children,
}: PipelineStageDropdownProps): ReactElement {
  const copy = useListCopy()
  const sortedStages = useMemo(() => [...stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [stages])
  const currentColor = stages.find((stage) => stage.name === currentStage)?.color

  return (
    // `modal={false}` is required inside the contact drawer. A modal Radix menu
    // sets `pointer-events: none` on <body> while open, so a click that dismisses
    // the menu resolves its target up to <html>. The drawer's global outside-click
    // handler (DrawerV2) then sees a target it doesn't contain and closes the whole
    // drawer. Non-modal keeps the dismiss click's real target, so clicking inside
    // the drawer only closes the menu.
    <DropdownMenu modal={false}>
      {/* stopPropagation: inside a table row the row's own click handler must not fire. */}
      {children ? (
        <DropdownMenuTrigger className={triggerClassName} onClick={(e) => e.stopPropagation()}>
          {children}
        </DropdownMenuTrigger>
      ) : (
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="field" size="xs" className="max-w-40">
            {currentStage && (
              <span className={`size-2 shrink-0 rounded-xs border-2 border-${currentColor || `strong`}`} />
            )}
            <span className={cn(`min-w-0 truncate`, !currentStage && `text-minimal`)}>
              {currentStage || `Set stage`}
            </span>
            <NavArrowDown className="ml-auto size-3 text-subtle" strokeWidth={2} />
          </Button>
        </DropdownMenuTrigger>
      )}
      {/* `bg-surface-raised` overrides the default `bg-popover` token, which is a light gray
          in light mode. Other dropdown portals (ComboBox, tags) override it the same way. */}
      <DropdownMenuContent align="start" className="w-48 bg-surface-raised" onClick={(e) => e.stopPropagation()}>
        {sortedStages.map((s) => (
          <DropdownMenuItem
            key={s.name}
            onSelect={() => s.name !== currentStage && onChange(s.name)}
            className={`flex items-center gap-2 text-body-xs ${
              s.name === currentStage ? `font-semibold text-strong` : `text-default`
            }`}
          >
            <span className={`h-2 w-2 shrink-0 rounded-[2px] border-2 border-${s.color || `strong`}`} />
            <span className="truncate">{s.name}</span>
          </DropdownMenuItem>
        ))}
        {onRemove && (
          <>
            {sortedStages.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onSelect={onRemove}
              className="flex items-center gap-2 text-body-xs text-red-600 focus:bg-red-50 focus:text-red-600"
            >
              <Trash className="h-3.5 w-3.5 text-red-600" strokeWidth={2} />
              {copy.removeFromList}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
